import { Skeleton, SkeletonCircle } from '@chakra-ui/react';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { CandidateTechStacksType } from '@/types';
import { PersonType } from '@/types/api';
import * as Styles from './CandidateListCard.styles';
import Menu from '../Menu';
import { MenuItemProps } from '../Menu/Menu.component';

type CandidateListCardProps = {
  person: Pick<
    PersonType,
    'firstName' | 'lastName' | 'pictureUrl' | 'source' | 'candidateOwner' | 'status' | 'skills'
  >;
  displaySourceInfo?: boolean;
  border?: Styles.BorderVariation;
  menuItems?: MenuItemProps[];
  className?: string;
};

const CandidateListCard = ({
  person,
  displaySourceInfo = false,
  border,
  menuItems,
  ...props
}: CandidateListCardProps): JSX.Element => (
  <Styles.Box border={border} {...props}>
    <Styles.Thumbnail
      pictureUrl={person.pictureUrl || undefined}
      firstName={person.firstName}
      lastName={person.lastName}
      size={48}
    />
    <Styles.InfoBox>
      <Styles.TitleContainer withSourceInfo={displaySourceInfo}>
        <Styles.Title>{`${person.firstName}${
          person.lastName ? ` ${person.lastName}` : ''
        }`}</Styles.Title>
        {displaySourceInfo ? (
          <Styles.Managerinfo>
            {person.source && <p>{person.source}</p>}
            {person.candidateOwner && <p>{`${person.candidateOwner.split(' ')[0]}’s`}</p>}
          </Styles.Managerinfo>
        ) : (
          <>{person.status && <Styles.Tag outlined status={person.status} />}</>
        )}
      </Styles.TitleContainer>
      {((person.skills || []).length > 0 || displaySourceInfo) && (
        <Styles.TechnologiesList>
          {displaySourceInfo && person.status && <Styles.Tag outlined status={person.status} />}
          {(person.skills || []).map((skill: CandidateTechStacksType | string) => {
            if (typeof skill === 'string') {
              return <p key={skill}>{skill}</p>;
            }

            return <p key={skill.id}>{skill.skill.name}</p>;
          })}
        </Styles.TechnologiesList>
      )}
    </Styles.InfoBox>
    {menuItems && (
      <Styles.MenuBox>
        <Menu icon={MoreHorizIcon} menuItems={menuItems} />
      </Styles.MenuBox>
    )}
  </Styles.Box>
);

const CandidateListCardSkeleton = ({
  border,
  className,
  displaySourceInfo,
}: Pick<CandidateListCardProps, 'border' | 'className' | 'displaySourceInfo'>): JSX.Element => (
  <Styles.Box border={border} className={className}>
    <SkeletonCircle w={12} h={12} className="tw-mr-3" />
    <Styles.InfoBox>
      <Styles.TitleContainer withSourceInfo={displaySourceInfo}>
        <Skeleton className="tw-h-4 tw-w-[100px] tw-my-[1.6px]" />
        {!displaySourceInfo && <Skeleton className="tw-w-[60px] tw-h-4.5" />}
      </Styles.TitleContainer>
      <Styles.TechnologiesList>
        <Skeleton className="tw-w-[56px] tw-h-5" />
        <Skeleton className="tw-w-[32px] tw-h-5" />
      </Styles.TechnologiesList>
    </Styles.InfoBox>
  </Styles.Box>
);

CandidateListCard.Skeleton = CandidateListCardSkeleton;

export default CandidateListCard;
